/**
 * The UI permission catalog — every key the panel can gate a route, nav item
 * or component on, with a human description. This file IS the contract with
 * the backend: it is exported as JSON (`bun run permissions:export`), the
 * backend grants subjects these keys, and `GET` my-permissions answers the
 * subset the current user holds (see `api/access-api.ts`).
 *
 * Key shape: `<domain>[.<sub>].<action>`, lowercase snake_case segments. Name
 * a key after the CAPABILITY, not the component that shows it — one key
 * (`orders.finish`) gates the table action, the detail button and the chat
 * icon alike, and survives component refactors.
 *
 * Grants may use wildcards: `*` (everything) or `<prefix>.*` (`orders.*`
 * covers `orders.view`, `orders.detail.view`, `orders.finish`, …).
 */
export const PERMISSION_CATALOG = {
  // ── Pages (route `staticData.permission`; the sidebar hides what they gate)
  'dashboard.view': 'Dashboard (home page)',
  'analytics.view': 'Analytics page',
  'dispatch.view': 'Dispatch board',
  'inbox.view': 'Support inbox (chats)',
  'merchants.view': 'Merchants page: businesses, clients, employees, merchant detail',
  'integration_tokens.view': 'Integration tokens page',
  'services.view': 'Service settings page',
  'delivery_settings.view': 'Delivery settings page (search service)',
  'eta_settings.view': 'ETA settings page (search service)',
  'multiorder_params.view': 'Multiorder parameters page (search service)',
  'cities.view': 'Cities and dispatch grid page (search service)',
  'orders.view': 'Delivery orders list',
  'orders.detail.view': 'Order detail page (opened from any orders list or chat)',
  'orders.edit': 'Order edit page',
  'couriers.view': 'Couriers list and courier detail',
  'slots.view': 'Bonus slots page',
  'reports.transactions.view': 'Transactions report page',
  'reports.self_employed.view': 'Self-employed couriers report page',
  'food_orders.view': 'Food orders page',
  'catalog.restaurants.view': 'Catalog management: restaurants',
  'catalog.categories.view': 'Catalog management: categories',
  'catalog.products.view': 'Catalog management: products',
  'catalog.promotions.view': 'Catalog management: promotions',
  'business.categories.view': 'Business categories page',
  'business.banners.view': 'Banners pages (business, product, restaurant)',
  'moderation.restaurants.view': 'Moderation: restaurants',
  'moderation.products.view': 'Moderation: products',
  'moderation.categories.view': 'Moderation: categories',
  'moderation.promotions.view': 'Moderation: promotions',
  'storefront.restaurants.view': 'Storefront: restaurants',
  'storefront.products.view': 'Storefront: products',
  'storefront.categories.view': 'Storefront: categories',
  'storefront.promotions.view': 'Storefront: promotions',
  'users.view': 'System users page',
  'rbac.view': 'Access control (RBAC) page',

  // ── Actions & sections inside pages (`<Can>` / `useCan`)
  'merchants.edit': 'Edit a merchant',
  'merchants.actions.view': 'View merchant info section',
  'orders.finish': 'Finish an order; open the order chat',
  'orders.make_debt': 'Make debt action on the order detail',
  'couriers.activate': 'Unblock a courier',
  'couriers.deactivate': 'Block a courier',
  'couriers.block_orders': 'Choose the "orders" scope when blocking a courier',
  'couriers.block_box': 'Choose the "box" scope when blocking a courier',
  'couriers.return_hold': 'Return held money to a courier',
  'couriers.deal.view': 'Contract (deal) column in the couriers table',
  'couriers.deal.activate': 'Activate a courier contract (deal)',
  'couriers.manage_order': 'Courier reorder / return actions on an order',
  'clients.actions':
    'Vendor actions: tariff, discount, account type, invoices, min balance',
  'clients.sanction': 'Sanction a client',
  'clients.manage': 'Toggle client order blocking; edit the sales manager',
  'clients.delete': 'Delete a client',
  'delivery_settings.publish': 'Publish a new delivery settings version',
  'eta_settings.publish': 'Publish a new ETA settings version',
  'multiorder_params.publish': 'Publish a new multiorder parameters version',
  'cities.manage': 'Create/edit cities and block grid boxes',

  // Additional capabilities exposed by page and feature controls.
  'merchants.create': 'Create a merchant',
  'merchants.employees.manage': 'Attach, edit and detach merchant employees',
  'clients.export': 'Export clients and businesses',
  'clients.edit': 'Edit a client or employee profile',
  'orders.notes.view': 'View order notes',
  'orders.courier_payment': 'Change courier product payment status',
  'food_orders.create_test': 'Create a test food order',
  'catalog.restaurants.create': 'Create a catalog restaurant',
  'catalog.restaurants.edit': 'Edit a catalog restaurant',
  'catalog.restaurants.delete': 'Delete a catalog restaurant',
  'catalog.categories.create': 'Create a product category',
  'catalog.categories.edit': 'Edit a product category',
  'catalog.categories.delete': 'Delete a product category',
  'catalog.products.create': 'Create a catalog product',
  'catalog.products.edit': 'Edit a catalog product',
  'catalog.products.delete': 'Delete a catalog product',
  'catalog.promotions.create': 'Create a catalog promotion',
  'catalog.promotions.edit': 'Edit a catalog promotion',
  'catalog.promotions.delete': 'Delete a catalog promotion',
  'inbox.notes.delete': 'Delete internal support notes',
  'users.ban': 'Block a system user',
  'users.unban': 'Unblock a system user',
  'users.delete': 'Delete a system user',
  'users.password.reset': 'Reset a system user password',
  'orders.cancel': 'Cancel an order and refund courier transactions',
  'orders.payment': 'Change order payment to balance',
  'orders.notes.create': 'Create order notes',
  'orders.priority.manage': 'Change order status priority',
  'orders.export': 'Export orders',
  'couriers.edit': 'Edit a courier profile',
  'couriers.export': 'Export couriers',
  'couriers.contract.edit': 'Edit courier contract details',
  'couriers.sync': 'Synchronize courier reserve balance',
  'couriers.chat': 'Create or open a courier support chat',
  'couriers.bonus': 'Award a courier bonus',
  'couriers.foul': 'Apply a courier fine',
  'couriers.limit_balance': 'Change courier balance limits',
  'couriers.transactions.cancel': 'Cancel a courier transaction',
  'slots.create': 'Create a bonus slot',
  'slots.edit': 'Edit a bonus slot',
  'slots.delete': 'Delete a bonus slot',
  'reports.transactions.export': 'Download the transactions, orders and battery reports',
  'reports.self_employed.export': 'Export the self-employed couriers report',
  'users.create': 'Create a system user',
  'users.edit': 'Edit a system user',
  'users.sessions.view': 'View system user sessions',
  'users.sessions.revoke': 'Revoke system user sessions',
  'integration_tokens.generate': 'Generate or regenerate integration tokens',
  'services.edit': 'Edit service fees',
  'rbac.features.create': 'Register a UI feature',
  'rbac.features.edit': 'Edit a UI feature',
  'rbac.features.delete': 'Delete a UI feature',
  'rbac.grants.manage': 'Grant or revoke role and employee access',
  'food_orders.cancel': 'Cancel a food order',
  'inbox.reply': 'Reply to and acquire support chats',
  'inbox.transfer': 'Transfer a chat to another operator',
  'inbox.close': 'Close a support chat',
  'inbox.notes.create': 'Add internal support notes',
  'inbox.macros.manage': 'Create, edit and delete chat macros',
  'clients.transactions.cancel': 'Cancel a client transaction',
  'business.categories.create': 'Create business categories',
  'business.categories.edit': 'Edit business categories',
  'business.categories.delete': 'Delete business categories',
  'business.banners.create': 'Create business banners',
  'business.banners.edit': 'Edit business banners',
  'business.banners.delete': 'Delete business banners',
  'moderation.restaurants.approve': 'Approve restaurants moderation requests',
  'moderation.restaurants.reject': 'Reject restaurants moderation requests',
  'storefront.restaurants.publish': 'Publish storefront restaurants',
  'storefront.restaurants.unpublish': 'Unpublish storefront restaurants',
  'moderation.products.approve': 'Approve products moderation requests',
  'moderation.products.reject': 'Reject products moderation requests',
  'storefront.products.publish': 'Publish storefront products',
  'storefront.products.unpublish': 'Unpublish storefront products',
  'moderation.categories.approve': 'Approve categories moderation requests',
  'moderation.categories.reject': 'Reject categories moderation requests',
  'storefront.categories.publish': 'Publish storefront categories',
  'storefront.categories.unpublish': 'Unpublish storefront categories',
  'moderation.promotions.approve': 'Approve promotions moderation requests',
  'moderation.promotions.reject': 'Reject promotions moderation requests',
  'storefront.promotions.publish': 'Publish storefront promotions',
  'storefront.promotions.unpublish': 'Unpublish storefront promotions',
} as const satisfies Record<string, string>;

export type PermissionKey = keyof typeof PERMISSION_CATALOG;

/** Every catalog key, in declaration order. */
export const PERMISSION_KEYS = Object.keys(PERMISSION_CATALOG) as PermissionKey[];
